import type { CookieAttributes } from 'js-cookie';

import Cookies from 'js-cookie';
import { chunk } from 'lodash';

/**
 * In bytes, max browser cookie size is 4096 bytes (name + value)
 * Safely assume no unicode values hence 1 char = 1 bytes
 * Reserve name size to be 96 bytes/char and value size to be 4000 bytes/char
 */
export const MAX_COOKIE_NAME_SIZE = 96;
export const MAX_COOKIE_VALUE_SIZE = 4000;

export const findCookiesByName = function(name: string): { name: string, value: string }[] {
  // Retrieve all cookies
  const allCookies = Cookies.get();

  // Select target cookie names
  const filteredCookieNames = Object.keys(allCookies).filter((key) => key.indexOf(name) > -1);

  // As cookies are stored as a map, order is not guaranteed, hence we perform sorting using the index prefix
  const sortedCookieNames = filteredCookieNames.sort((cookieA, cookieB) => {
    const [strIdxA] = cookieA.split('-');
    const [strIdxB] = cookieB.split('-');
    const idxA = Number(strIdxA);
    const idxB = Number(strIdxB);
    if (idxA < idxB) return -1;
    if (idxA > idxB) return 1;
    return 0;
  });

  // Return as name/value struct array
  return sortedCookieNames.map((name) => ({ name, value: allCookies[name] }));
};

export const getCookie = function(name: string): string {
  // If we manage to get exactly one, it means that it is not split into blocks
  const cookie = Cookies.get(name);
  if (cookie) return cookie;

  // Exact retrieval not available, cookie has been split into blocks
  const cookies = findCookiesByName(name);
  return cookies.map(({ value }) => value).join('');
};

export const setCookie = function(name: string, value: string, options?: CookieAttributes) {
  if (!name || !value) return;

  // Ensure that name doesn't overflow our max name size
  if (name.length > MAX_COOKIE_NAME_SIZE) {
    console.error(`Invalid cookie name size, expected: ${MAX_COOKIE_NAME_SIZE} bytes, found ${name.length} bytes`);
    return;
  }

  // Perform normal cookie setter if value is less than max cookie size
  if (value.length <= MAX_COOKIE_VALUE_SIZE) {
    Cookies.set(name, value, options);
    return;
  }

  // Split value into smaller chunks and store in multiple cookies
  const blocks = chunk(value.split(''), MAX_COOKIE_VALUE_SIZE).map((chuncks) => chuncks.join(''));
  blocks.forEach((block, idx) => {
    // Add an index prefix to the cookie name, which will be used for sorting during retrieval
    Cookies.set(`${idx}-${name}`, block, options);
  });
};

export const removeCookie = function(name: string, options?: CookieAttributes) {
  // If we manage to get exactly one, it means that it is not split into blocks
  const cookie = Cookies.get(name);
  if (cookie) {
    Cookies.remove(name, options);
    return;
  }

  // Exact retrieval not available, cookie has been split into blocks
  const cookies = findCookiesByName(name);
  cookies.forEach(({ name }) => Cookies.remove(name));
};
